/**
 * codeBlock 구문강조 + 언어 선택 컨트롤러 (A1, 무의존).
 *
 * 강조는 "시각 전용"이다 — 직렬화는 .content의 textContent(htmlToInlineContent) 기반이라
 * tok span 유무와 무관하게 동일 결과(라운드트립 무영향).
 *
 * 동작:
 *  - 마운트/refresh: 포커스되지 않은 모든 codeBlock을 강조하고 언어 셀렉터를 보장.
 *  - 포커스 중: DOM을 건드리지 않음(caret/IME 안전, 새 입력은 블러 전까지 무채색).
 *  - 블러: 해당 codeBlock 재강조.
 *  - 읽기전용: 항상 강조(포커스 불가).
 */
import { htmlToInlineContent } from "../../serialize/inline.js";
import { highlightToHtml, CODE_LANGUAGES } from "./highlight.js";
import { normalizeCodeTrailingNewline } from "../../editing.js";

function contentOf(block) {
  return block.querySelector(":scope > .content");
}

// .content의 실제 코드 텍스트(<br>→"\n" 포함) 추출
function readCode(content) {
  return htmlToInlineContent(content)
    .map((n) => (n && n.text != null ? n.text : ""))
    .join("");
}

export function attachCodeHighlight(editorEl, ctx) {
  const onChange = (ctx && ctx.onChange) || (() => {});

  function highlight(block) {
    const content = contentOf(block);
    if (!content) return;
    if (content.contains(document.activeElement)) return; // 편집(.content) 포커스 중엔 건드리지 않음(caret/IME 안전)
    const lang = (block.__props && block.__props.language) || "";
    content.innerHTML = highlightToHtml(readCode(content), lang);
    normalizeCodeTrailingNewline(content); // 후행 개행 빈 줄 렌더 보정(pre-wrap)
  }

  function ensureSelector(block) {
    let sel = block.querySelector(":scope > .lumir-code-lang");
    if (!sel) {
      sel = document.createElement("select");
      sel.className = "lumir-code-lang";
      sel.setAttribute("contenteditable", "false");
      sel.setAttribute("aria-label", "코드 언어");
      for (const { value, label } of CODE_LANGUAGES) {
        const opt = document.createElement("option");
        opt.value = value;
        opt.textContent = label;
        sel.appendChild(opt);
      }
      sel.addEventListener("change", () => {
        if (!block.__props) block.__props = {};
        block.__props.language = sel.value;
        highlight(block);
        onChange();
      });
      sel.addEventListener("mousedown", (e) => e.stopPropagation()); // 셀렉터 클릭이 블록 선택/드래그로 번지지 않게
      block.insertBefore(sel, block.firstChild);
    }
    sel.value = (block.__props && block.__props.language) || "";
  }

  function scan() {
    editorEl.querySelectorAll('.block[data-type="codeBlock"]').forEach((block) => {
      ensureSelector(block);
      highlight(block);
    });
  }

  function onFocusOut(e) {
    const content = e.target && e.target.closest && e.target.closest(".content");
    if (!content) return;
    const block = content.closest('.block[data-type="codeBlock"]');
    if (!block || !editorEl.contains(block)) return;
    // 포커스가 .content를 떠난 뒤 재강조(highlight 내부에서 content 포커스 재확인)
    setTimeout(() => highlight(block), 0);
  }

  editorEl.addEventListener("focusout", onFocusOut);
  scan();

  return {
    destroy: () => editorEl.removeEventListener("focusout", onFocusOut),
    refresh: scan,
  };
}
